import { useMessageTemplates, fillTemplate, WEDDING_TEMPLATES } from '../lib/messageTemplates';
import { openMessageAfter } from '../lib/whatsapp';
import { RUNTIME_BACKEND_URL } from '../lib/backendUrl';
import { useEffect, useState, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useDataStore } from '../store/dataStore';
import { useAuthStore } from '../store/authStore';
import { formatDate } from '../lib/utils';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import { toast } from 'sonner';
import PanoramicAlbumWizard from '../components/PanoramicAlbumWizard';
import PrintOrderDialog from '../components/PrintOrderDialog';
import {
  ArrowLeft,
  Upload,
  Image,
  Trash2,
  Download,
  Link2,
  Send,
  Check,
  Loader2,
  FolderOpen,
  Eye,
  X,
  Clock,
  Printer,
  CheckCircle2,
  BookOpen,
  Palette,
  User,
  Calendar,
  AlertTriangle,
  BookImage,
  Edit
} from 'lucide-react';

const BACKEND_URL = RUNTIME_BACKEND_URL;

// Durum seçenekleri
const STATUS_OPTIONS = [
  { id: 'pending', label: 'Beklemede', icon: Clock, color: 'bg-gray-500' },
  { id: 'processing', label: 'İşlemde', icon: Loader2, color: 'bg-amber-500' },
  { id: 'printing', label: 'Baskıda', icon: Printer, color: 'bg-purple-500' },
  { id: 'completed', label: 'Tamamlandı', icon: CheckCircle2, color: 'bg-green-500' },
];

export default function AlbumPage() {
  const { customerId } = useParams();
  const navigate = useNavigate();
  const fileInputRef = useRef(null);
  
  const {
    customers,
    fetchCustomers,
    currentAlbum,
    albumPhotos,
    albumPhotosLoading,
    fetchAlbumByCustomer,
    fetchAlbumPhotos,
    createAlbum,
    uploadPhotos,
    deletePhoto,
    deleteAlbum,
    downloadSelectedPhotos,
    sendNotification,
    activeUploads,
    setUploadProgress,
    clearUpload,
    cancelUpload,
    listSubfolders,
    renameSubfolder,
    movePhotosToSubfolder
  } = useDataStore();

  const [isCreating, setIsCreating] = useState(false);
  const [albumName, setAlbumName] = useState('');
  const [selectedPreview, setSelectedPreview] = useState(null);
  const [isSendingLink, setIsSendingLink] = useState(false);
  const [updatingStatus, setUpdatingStatus] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const [downloadProgress, setDownloadProgress] = useState(0);
  const [downloadStats, setDownloadStats] = useState({ speed: 0, downloaded: 0, total: 0, eta: 0 });
  const [downloadController, setDownloadController] = useState(null);
  const [selectedPhotos, setSelectedPhotos] = useState(new Set());
  const [isDeleting, setIsDeleting] = useState(false);

  // Alt-klasör (subfolder) state
  const [currentSubfolder, setCurrentSubfolder] = useState(''); // Aktif alt-klasör (boş = ana albüm)
  const [subfolders, setSubfolders] = useState([]);              // [{name, count, selected_count}]
  const [showNewSubfolderDialog, setShowNewSubfolderDialog] = useState(false);
  const [newSubfolderName, setNewSubfolderName] = useState('');
  const [showMoveDialog, setShowMoveDialog] = useState(false);
  const [moveTargetSubfolder, setMoveTargetSubfolder] = useState('');

  // Alt-klasör listesini yükle
  const loadSubfolders = async () => {
    if (!currentAlbum?.id) return;
    try {
      const list = await listSubfolders(currentAlbum.id);
      setSubfolders(list || []);
    } catch { /* noop */ }
  };
  useEffect(() => { loadSubfolders(); /* eslint-disable-next-line */ }, [currentAlbum?.id, albumPhotos.length]);
  
  // Yükleme iptal ve aynı isim kontrolü için state'ler
  const [duplicateFiles, setDuplicateFiles] = useState([]);
  const [pendingFiles, setPendingFiles] = useState([]);
  const [showDuplicateDialog, setShowDuplicateDialog] = useState(false);
  
  const { token, user: authUser } = useAuthStore();
  const tpl = useMessageTemplates('/api', token, WEDDING_TEMPLATES);
  const customer = customers.find(c => c.id === customerId);
  
  // Global upload state'den bu albümün durumunu al
  const currentUpload = currentAlbum ? activeUploads[currentAlbum.id] : null;
  const isUploading = currentUpload && !currentUpload.cancelled && currentUpload.current < currentUpload.total;
  const uploadProgress = currentUpload || { current: 0, total: 0, percent: 0 };
  const estimatedTimeLeft = currentUpload?.estimatedTimeLeft;
  const uploadCancelled = currentUpload?.cancelled;
  
  // Panoramik Albüm Sihirbazı state
  const [showPanoramicWizard, setShowPanoramicWizard] = useState(false);

  // Baskı Siparişi Dialog state
  const [showPrintDialog, setShowPrintDialog] = useState(false);

  // Fotoğraf seçim fonksiyonları
  const togglePhotoSelection = (photoId) => {
    setSelectedPhotos(prev => {
      const newSet = new Set(prev);
      if (newSet.has(photoId)) {
        newSet.delete(photoId);
      } else {
        newSet.add(photoId);
      }
      return newSet;
    });
  };

  const selectAllPhotos = () => {
    if (selectedPhotos.size === albumPhotos.filter(p => (p.subfolder || '') === currentSubfolder).length) {
      setSelectedPhotos(new Set());
    } else {
      setSelectedPhotos(new Set(albumPhotos.filter(p => (p.subfolder || '') === currentSubfolder).map(p => p.id)));
    }
  };

  const deleteSelectedPhotos = async () => {
    if (selectedPhotos.size === 0) return;
    
    const confirmed = window.confirm(`${selectedPhotos.size} fotoğrafı silmek istediğinize emin misiniz?`);
    if (!confirmed) return;
    
    setIsDeleting(true);
    let successCount = 0;
    let errorCount = 0;
    
    try {
      for (const photoId of selectedPhotos) {
        try {
          await deletePhoto(currentAlbum.id, photoId);
          successCount++;
        } catch (error) {
          errorCount++;
        }
      }
      
      if (errorCount === 0) {
        toast.success(`${successCount} fotoğraf silindi`);
      } else {
        toast.warning(`${successCount} silindi, ${errorCount} başarısız`);
      }
      
      setSelectedPhotos(new Set());
      fetchAlbumPhotos(currentAlbum.id);
    } catch (error) {
      toast.error('Silme işlemi başarısız');
    } finally {
      setIsDeleting(false);
    }
  };

  // Durum güncelleme fonksiyonu
  const updateAlbumStatus = async (newStatus) => {
    if (!currentAlbum) return;
    
    setUpdatingStatus(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/albums/${currentAlbum.id}/status`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ status: newStatus })
      });
      
      if (!res.ok) throw new Error('Durum güncellenemedi');
      
      const data = await res.json();
      toast.success(data.message);
      fetchAlbumByCustomer(customerId);
    } catch (error) {
      toast.error(error.message);
    } finally {
      setUpdatingStatus(false);
    }
  };

  // Sayfa kapatma uyarısı
  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (isUploading) {
        e.preventDefault();
        e.returnValue = 'Fotoğraf yükleme devam ediyor. Sayfadan çıkmak istediğinize emin misiniz?';
        return e.returnValue;
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [isUploading]);

  useEffect(() => {
    if (customers.length === 0) {
      fetchCustomers();
    }
  }, [customers.length, fetchCustomers]);

  useEffect(() => {
    if (customerId) {
      loadAlbum();
    }
  }, [customerId]);

  const loadAlbum = async () => {
    const album = await fetchAlbumByCustomer(customerId);
    if (album) {
      await fetchAlbumPhotos(album.id);
    }
  };

  const handleCreateAlbum = async () => {
    if (!albumName.trim()) {
      toast.error('Albüm adı girin');
      return;
    }

    setIsCreating(true);
    try {
      const album = await createAlbum(customerId, albumName, '');
      toast.success('Albüm oluşturuldu');
      setAlbumName('');
      // Refresh to show the album immediately
      await fetchAlbumByCustomer(customerId);
      if (album) {
        await fetchAlbumPhotos(album.id);
      }
    } catch (error) {
      toast.error(error.message);
    } finally {
      setIsCreating(false);
    }
  };

  const handleFileSelect = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;

    // Mevcut fotoğraf isimleriyle karşılaştır
    const existingNames = albumPhotos.map(p => p.original_name?.toLowerCase() || '');
    const duplicates = files.filter(f => existingNames.includes(f.name.toLowerCase()));
    const nonDuplicates = files.filter(f => !existingNames.includes(f.name.toLowerCase()));
    
    if (duplicates.length > 0) {
      // Aynı isimde fotoğraflar var - kullanıcıya sor
      setDuplicateFiles(duplicates);
      setPendingFiles(files);
      setShowDuplicateDialog(true);
      // Input'u temizle
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
      return;
    }

    // Duplikat yoksa direkt yükle
    await startUpload(files);
  };

  // Sadece yeni (farklı isimli) fotoğrafları yükle
  const handleUploadOnlyNew = async () => {
    setShowDuplicateDialog(false);
    const existingNames = albumPhotos.map(p => p.original_name?.toLowerCase() || '');
    const nonDuplicates = pendingFiles.filter(f => !existingNames.includes(f.name.toLowerCase()));
    
    if (nonDuplicates.length > 0) {
      await startUpload(nonDuplicates);
    } else {
      toast.info('Yüklenecek yeni fotoğraf yok');
    }
    setDuplicateFiles([]);
    setPendingFiles([]);
  };

  // İsim değiştirerek yükle (foto.jpg -> foto_1.jpg)
  const handleUploadWithRename = async () => {
    setShowDuplicateDialog(false);
    const existingNames = albumPhotos.map(p => p.original_name?.toLowerCase() || '');
    
    // Tüm dosyaları yükle, aynı isimdekilerin ismini değiştir
    const renamedFiles = pendingFiles.map(file => {
      if (existingNames.includes(file.name.toLowerCase())) {
        // İsmi değiştir
        const ext = file.name.includes('.') ? '.' + file.name.split('.').pop() : '';
        const baseName = file.name.replace(ext, '');
        const newName = `${baseName}_${Date.now()}${ext}`;
        // Yeni File objesi oluştur
        return new File([file], newName, { type: file.type });
      }
      return file;
    });
    
    await startUpload(renamedFiles);
    setDuplicateFiles([]);
    setPendingFiles([]);
  };

  // Duplikat iptal edildi
  const handleCancelDuplicateUpload = () => {
    setShowDuplicateDialog(false);
    setDuplicateFiles([]);
    setPendingFiles([]);
  };

  // Yükleme işlemini iptal et
  const handleCancelUpload = () => {
    if (window.confirm('Yükleme iptal edilsin mi? Şimdiye kadar yüklenen fotoğraflar kalacak.')) {
      if (currentAlbum) {
        cancelUpload(currentAlbum.id);
      }
    }
  };

  // Sayfa çıkış uyarısı
  useEffect(() => {
    const handleBeforeUnload = (e) => {
      // Herhangi bir aktif yükleme var mı kontrol et
      const hasActiveUpload = Object.values(activeUploads).some(u => !u.cancelled && u.current < u.total);
      if (hasActiveUpload) {
        e.preventDefault();
        e.returnValue = 'Yükleme devam ediyor! Sayfadan çıkarsanız yükleme iptal olacak.';
        return e.returnValue;
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [activeUploads]);

  // Asıl yükleme fonksiyonu
  const startUpload = async (files) => {
    if (!currentAlbum) return;
    
    const albumId = currentAlbum.id;
    const startTime = Date.now();
    
    // Global state'e başlangıç bilgisini kaydet
    setUploadProgress(albumId, {
      customerId,
      customerName: customer ? `${customer.bride_name} & ${customer.groom_name}` : 'Bilinmiyor',
      albumName: currentAlbum.name,
      current: 0,
      total: files.length,
      percent: 0,
      estimatedTimeLeft: null,
      cancelled: false
    });

    let successCount = 0;
    let errorCount = 0;
    let cancelled = false;

    try {
      for (let i = 0; i < files.length; i++) {
        // İptal kontrolü - global state'den oku
        const currentState = useDataStore.getState().activeUploads[albumId];
        if (currentState?.cancelled) {
          cancelled = true;
          break;
        }

        const file = files[i];
        
        try {
          await uploadPhotos(albumId, [file], currentSubfolder);
          successCount++;
        } catch (error) {
          errorCount++;
          console.error(`Yükleme hatası (${file.name}):`, error);
        }

        // Progress güncelle
        const current = i + 1;
        const percent = Math.round((current / files.length) * 100);
        
        // Kalan süre tahmini
        const elapsed = Date.now() - startTime;
        const avgTimePerFile = elapsed / current;
        const remaining = files.length - current;
        const estimatedMs = remaining * avgTimePerFile;
        
        let timeLeft = null;
        if (remaining <= 0 || estimatedMs <= 0) {
          timeLeft = 'Bitiyor...';
        } else if (estimatedMs > 60000) {
          timeLeft = `~${Math.ceil(estimatedMs / 60000)} dk`;
        } else if (estimatedMs > 1000) {
          timeLeft = `~${Math.round(estimatedMs / 1000)} sn`;
        } else {
          timeLeft = 'Bitiyor...';
        }
        
        setUploadProgress(albumId, { current, percent, estimatedTimeLeft: timeLeft });
      }

      // Sonuç mesajı
      if (cancelled) {
        toast.warning(`Yükleme iptal edildi. ${successCount} fotoğraf yüklendi.`);
      } else if (errorCount === 0) {
        toast.success(`${successCount} fotoğraf başarıyla yüklendi`);
      } else {
        toast.warning(`${successCount} yüklendi, ${errorCount} başarısız`);
      }

      await fetchAlbumPhotos(albumId);
      await fetchAlbumByCustomer(customerId);
    } catch (error) {
      toast.error(error.message);
    } finally {
      // Yükleme bitti, state'i temizle
      clearUpload(albumId);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleDeletePhoto = async (photoId) => {
    if (!window.confirm('Bu fotoğrafı silmek istediğinize emin misiniz?')) return;
    
    try {
      await deletePhoto(currentAlbum.id, photoId);
      toast.success('Fotoğraf silindi');
      await fetchAlbumByCustomer(customerId);
    } catch (error) {
      toast.error(error.message);
    }
  };

  const handleDeleteAlbum = async () => {
    if (!window.confirm('Bu albümü ve tüm fotoğrafları silmek istediğinize emin misiniz?')) return;
    
    try {
      await deleteAlbum(currentAlbum.id);
      toast.success('Albüm silindi');
      navigate('/panel/musteriler');
    } catch (error) {
      toast.error(error.message);
    }
  };

  const handleDownloadSelected = async () => {
    const controller = new AbortController();
    setDownloadController(controller);
    try {
      setIsDownloading(true);
      setDownloadProgress(0);
      setDownloadStats({ speed: 0, downloaded: 0, total: 0, eta: 0 });
      
      const startTime = Date.now();
      
      const response = await fetch(`${RUNTIME_BACKEND_URL}/api/albums/${currentAlbum.id}/download-selected`, {
        headers: {
          'Authorization': `Bearer ${token}`
        },
        signal: controller.signal
      });
      
      if (!response.ok) {
        throw new Error('İndirme başarısız');
      }
      
      // Get content length for progress
      const contentLength = response.headers.get('content-length');
      const total = parseInt(contentLength, 10) || 0;
      const totalMB = total > 0 ? (total / (1024 * 1024)).toFixed(1) : '?';
      
      // Get filename from header
      const disposition = response.headers.get('content-disposition');
      let filename = 'secilen_fotograflar.zip';
      if (disposition) {
        const filenameMatch = disposition.match(/filename\*=UTF-8''(.+)/);
        if (filenameMatch) {
          filename = decodeURIComponent(filenameMatch[1]);
        }
      }
      
      // Read response with progress
      const reader = response.body.getReader();
      const chunks = [];
      let receivedLength = 0;
      let lastUpdate = Date.now();
      let lastReceived = 0;
      
      // Initial stats with total
      setDownloadStats({ speed: 0, downloaded: '0', total: totalMB, eta: 0 });
      
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        
        chunks.push(value);
        receivedLength += value.length;
        
        const now = Date.now();
        const timeDiff = (now - lastUpdate) / 1000;
        
        if (timeDiff >= 0.5) {
          const bytesPerSecond = (receivedLength - lastReceived) / timeDiff;
          const speedMBps = bytesPerSecond / (1024 * 1024);
          const remainingBytes = total > 0 ? total - receivedLength : 0;
          const eta = bytesPerSecond > 0 ? Math.ceil(remainingBytes / bytesPerSecond) : 0;
          
          setDownloadStats({
            speed: speedMBps.toFixed(2),
            downloaded: (receivedLength / (1024 * 1024)).toFixed(1),
            total: totalMB,
            eta: total > 0 ? eta : 0
          });
          
          lastUpdate = now;
          lastReceived = receivedLength;
        }
        
        if (total > 0) {
          setDownloadProgress(Math.round((receivedLength / total) * 100));
        } else {
          // total bilinmiyorsa indirilen MB'ı göster
          setDownloadProgress(-1);
        }
      }
      
      // Create blob and download
      const blob = new Blob(chunks);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      
      toast.success('İndirme tamamlandı!');
    } catch (error) {
      if (error.name === 'AbortError') {
        toast.info('İndirme iptal edildi');
      } else {
        toast.error(error.message);
      }
    } finally {
      setIsDownloading(false);
      setDownloadProgress(0);
      setDownloadController(null);
    }
  };

  const cancelDownload = () => {
    if (downloadController) {
      downloadController.abort();
    }
  };

  const getGalleryUrl = () => {
    if (!customer?.gallery_token) return '';
    return `${window.location.origin}/galeri/${customer.gallery_token}`;
  };

  const handleCopyLink = async () => {
    const url = getGalleryUrl();
    try {
      await navigator.clipboard.writeText(url);
      toast.success('Link kopyalandı');
    } catch (err) {
      // Fallback for browsers without clipboard API
      const textArea = document.createElement('textarea');
      textArea.value = url;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      toast.success('Link kopyalandı');
    }
  };

  const handleSendLink = async (channel) => {
    const url = getGalleryUrl();
    const message = fillTemplate(tpl.get('galeri'), { gelin: customer?.bride_name, damat: customer?.groom_name, link: url, firma: authUser?.company_name || authUser?.full_name });
    
    setIsSendingLink(true);
    try {
      await openMessageAfter(channel, async () => {
        await sendNotification(customerId, message, channel);
        return { phone: customer?.bride_phone || customer?.groom_phone, text: message };
      });
      toast.success(`${channel === 'sms' ? 'SMS' : 'WhatsApp'} açıldı — mesaj hazır, göndere basın`);
    } catch (error) {
      toast.error(error.message);
    } finally {
      setIsSendingLink(false);
    }
  };

  const selectedCount = albumPhotos.filter(p => p.is_selected).length;

  return (
    <div className="space-y-6" data-testid="album-page">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="icon" onClick={() => navigate('/panel/musteriler')}>
          <ArrowLeft className="h-5 w-5" />
        </Button>
        <div className="flex-1">
          <h1 className="text-2xl sm:text-3xl font-bold">
            {customer ? `${customer.bride_name} & ${customer.groom_name}` : 'Albüm'}
          </h1>
          <p className="text-muted-foreground mt-1">
            {customer ? formatDate(customer.wedding_date) : ''}
          </p>
        </div>
      </div>

      {/* No Album - Create */}
      {!currentAlbum && (
        <Card className="animate-fade-in">
          <CardContent className="p-8 text-center">
            <FolderOpen className="h-16 w-16 mx-auto mb-4 text-muted-foreground/30" />
            <h3 className="text-lg font-medium mb-2">Henüz albüm yok</h3>
            <p className="text-muted-foreground mb-6">
              Bu müşteri için bir albüm oluşturun ve fotoğrafları yükleyin.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-3 max-w-md mx-auto">
              <Input
                placeholder="Albüm adı (örn: Düğün Fotoğrafları)"
                value={albumName}
                onChange={(e) => setAlbumName(e.target.value)}
                data-testid="album-name-input"
              />
              <Button onClick={handleCreateAlbum} disabled={isCreating} data-testid="create-album-btn">
                {isCreating ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Oluştur'}
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Album Exists */}
      {currentAlbum && (
        <>
          {/* Album Info & Actions */}
          <Card className="animate-fade-in">
            <CardContent className="p-4 sm:p-6">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                <div>
                  <h2 className="text-xl font-semibold">{currentAlbum.name}</h2>
                  <p className="text-sm text-muted-foreground mt-1">
                    {currentAlbum.photo_count} fotoğraf • {selectedCount} seçili
                  </p>
                </div>
                <div className="flex flex-wrap gap-2">
                  <input
                    type="file"
                    ref={fileInputRef}
                    onChange={handleFileSelect}
                    multiple
                    accept="image/*"
                    className="hidden"
                  />
                  <Button
                    onClick={() => fileInputRef.current?.click()}
                    disabled={isUploading}
                    data-testid="upload-photos-btn"
                  >
                    {isUploading ? (
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    ) : (
                      <Upload className="h-4 w-4 mr-2" />
                    )}
                    Fotoğraf Yükle
                  </Button>
                  
                  {selectedCount > 0 && (
                    <Button 
                      variant="outline" 
                      onClick={handleDownloadSelected} 
                      disabled={isDownloading}
                      data-testid="download-selected-btn"
                    >
                      {isDownloading ? (
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      ) : (
                        <Download className="h-4 w-4 mr-2" />
                      )}
                      {isDownloading ? `İndiriliyor... %${downloadProgress}` : `Seçilenleri İndir (${selectedCount})`}
                    </Button>
                  )}
                  
                  {/* Panoramik Albüm Butonu - Müşteri Seçimlerine Göre */}
                  {albumPhotos.filter(p => p.is_selected).length >= 5 && (
                    <Button
                      variant="outline"
                      className="border-primary text-primary hover:bg-primary hover:text-white"
                      onClick={() => setShowPanoramicWizard(true)}
                      data-testid="panoramic-album-btn"
                    >
                      <BookOpen className="h-4 w-4 mr-2" />
                      Panoramik Albüm ({albumPhotos.filter(p => p.is_selected).length})
                    </Button>
                  )}

                  {/* Albüm Baskısı Gönder Butonu */}
                  <Button
                    variant="outline"
                    className="border-emerald-500 text-emerald-700 hover:bg-emerald-500 hover:text-white"
                    onClick={() => setShowPrintDialog(true)}
                    data-testid="open-print-order-btn"
                  >
                    <Printer className="h-4 w-4 mr-2" />
                    Albüm Baskısı İçin Gönder
                  </Button>
                </div>
              </div>

              {/* Download Progress */}
              {isDownloading && (
                <div className="mt-4 p-4 bg-green-500/10 rounded-lg border border-green-500/30">
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <Download className="h-4 w-4 text-green-600 animate-bounce" />
                      <span className="font-medium text-sm text-green-700">
                        İndiriliyor... {downloadStats.total !== '?' ? `(${downloadStats.total} MB)` : ''}
                      </span>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className="text-sm font-bold text-green-600">
                        {downloadProgress >= 0 ? `%${downloadProgress}` : `${downloadStats.downloaded} MB`}
                      </span>
                      <Button
                        variant="destructive"
                        size="sm"
                        onClick={cancelDownload}
                        data-testid="cancel-download-btn"
                        className="h-7 px-2"
                      >
                        <X className="h-3 w-3 mr-1" />
                        İptal
                      </Button>
                    </div>
                  </div>
                  <div className="w-full bg-green-200 rounded-full h-3 mb-2">
                    <div 
                      className="bg-green-500 h-3 rounded-full transition-all duration-300"
                      style={{ width: downloadProgress >= 0 ? `${downloadProgress}%` : '100%' }}
                    />
                  </div>
                  <div className="flex items-center justify-between text-xs text-green-600">
                    <span>
                      {downloadStats.downloaded} MB / {downloadStats.total} MB
                    </span>
                    <span>
                      {downloadStats.speed} MB/s
                    </span>
                    <span>
                      {downloadStats.eta > 60 
                        ? `~${Math.ceil(downloadStats.eta / 60)} dk kaldı`
                        : downloadStats.eta > 0 
                          ? `~${downloadStats.eta} sn kaldı`
                          : downloadProgress >= 95 ? 'Tamamlanıyor...' : 'Hesaplanıyor...'
                      }
                    </span>
                  </div>
                </div>
              )}

              {/* Upload Progress */}
              {isUploading && (
                <div className="mt-4 p-4 bg-primary/5 rounded-lg border border-primary/20">
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <Loader2 className="h-4 w-4 animate-spin text-primary" />
                      <span className="font-medium text-sm">
                        Yükleniyor: {uploadProgress.current} / {uploadProgress.total}
                      </span>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className="text-sm text-muted-foreground">
                        {estimatedTimeLeft && `Kalan: ${estimatedTimeLeft}`}
                      </span>
                      <Button 
                        variant="destructive" 
                        size="sm"
                        onClick={handleCancelUpload}
                        disabled={uploadCancelled}
                      >
                        <X className="h-4 w-4 mr-1" />
                        {uploadCancelled ? 'İptal Ediliyor...' : 'İptal Et'}
                      </Button>
                    </div>
                  </div>
                  <div className="w-full bg-muted rounded-full h-2">
                    <div 
                      className="bg-primary h-2 rounded-full transition-all duration-300"
                      style={{ width: `${uploadProgress.percent}%` }}
                    />
                  </div>
                  <p className="text-xs text-muted-foreground mt-2 text-center">
                    ⚠️ Sayfa kapatmayın, yükleme devam ediyor...
                  </p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Gallery Link Actions */}
          <Card className="animate-fade-in stagger-1">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-lg">
                <Link2 className="h-5 w-5 text-primary" />
                Müşteri Galeri Linki
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center gap-2">
                <Input
                  value={getGalleryUrl()}
                  readOnly
                  className="flex-1 bg-muted"
                />
                <Button variant="outline" onClick={handleCopyLink}>
                  Kopyala
                </Button>
              </div>
              <div className="flex flex-wrap gap-2">
                <Button
                  variant="outline"
                  onClick={() => handleSendLink('sms')}
                  disabled={isSendingLink}
                  data-testid="send-sms-btn"
                >
                  <Send className="h-4 w-4 mr-2" />
                  SMS Gönder
                </Button>
                <Button
                  variant="outline"
                  onClick={() => handleSendLink('whatsapp')}
                  disabled={isSendingLink}
                  data-testid="send-whatsapp-btn"
                >
                  <Send className="h-4 w-4 mr-2" />
                  WhatsApp Gönder
                </Button>
                <Button
                  variant="outline"
                  onClick={() => window.open(getGalleryUrl(), '_blank')}
                >
                  <Eye className="h-4 w-4 mr-2" />
                  Önizle
                </Button>
              </div>
              <p className="text-xs text-muted-foreground">
                Bu linki müşterinizle paylaşın. Müşteri fotoğrafları görüntüleyip beğendiklerini seçebilir.
              </p>
            </CardContent>
          </Card>

          {/* Album Status & Details */}
          {currentAlbum.confirmed && (
            <Card className="animate-fade-in stagger-2 border-green-200 dark:border-green-800">
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-lg">
                  <CheckCircle2 className="h-5 w-5 text-green-600" />
                  Müşteri Seçimleri Onaylandı
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Status Selection */}
                <div className="space-y-2">
                  <Label className="text-sm font-medium">Albüm Durumu</Label>
                  <div className="flex flex-wrap gap-2">
                    {STATUS_OPTIONS.map((option) => {
                      const Icon = option.icon;
                      const isActive = currentAlbum.status === option.id;
                      return (
                        <Button
                          key={option.id}
                          variant={isActive ? 'default' : 'outline'}
                          size="sm"
                          onClick={() => updateAlbumStatus(option.id)}
                          disabled={updatingStatus}
                          className={isActive ? option.color : ''}
                        >
                          <Icon className={`h-4 w-4 mr-1 ${option.id === 'processing' && isActive ? 'animate-spin' : ''}`} />
                          {option.label}
                        </Button>
                      );
                    })}
                  </div>
                  {currentAlbum.status !== 'pending' && (
                    <p className="text-xs text-amber-600 dark:text-amber-400">
                      ⚠️ Müşteri artık seçim değişikliği yapamaz.
                    </p>
                  )}
                </div>

                {/* Album Details - Names and Date Only */}
                {(currentAlbum.album_names || currentAlbum.album_date) && (
                  <div className="p-4 bg-purple-50 dark:bg-purple-900/20 rounded-lg border border-purple-200 dark:border-purple-800">
                    <h4 className="font-medium text-sm mb-3 flex items-center gap-2">
                      <User className="h-4 w-4 text-purple-500" />
                      Albüm Detayları
                    </h4>
                    
                    {/* Modification Warnings */}
                    {(currentAlbum.names_modified || currentAlbum.date_modified) && (
                      <div className="mb-3 p-2 bg-red-50 dark:bg-red-900/20 rounded border border-red-200 dark:border-red-800">
                        <div className="flex items-start gap-2 text-red-600 dark:text-red-400">
                          <AlertTriangle className="h-4 w-4 mt-0.5 flex-shrink-0" />
                          <div className="text-sm">
                            <p className="font-medium">Dikkat: Müşteri bilgileri değiştirildi!</p>
                            <ul className="mt-1 text-xs list-disc list-inside">
                              {currentAlbum.names_modified && (
                                <li>İsimler düzeltildi (Orijinal: {currentAlbum.original_names})</li>
                              )}
                              {currentAlbum.date_modified && (
                                <li>Tarih değiştirildi (Orijinal: {currentAlbum.original_date})</li>
                              )}
                            </ul>
                          </div>
                        </div>
                      </div>
                    )}
                    
                    <div className="grid sm:grid-cols-2 gap-3 text-sm">
                      {currentAlbum.album_names && (
                        <div className="flex items-start gap-2">
                          <User className="h-4 w-4 text-muted-foreground mt-0.5" />
                          <div>
                            <span className="text-muted-foreground">İsimler: </span>
                            <span className={`font-medium ${currentAlbum.names_modified ? 'text-red-600' : ''}`}>
                              {currentAlbum.album_names}
                            </span>
                          </div>
                        </div>
                      )}
                      {currentAlbum.album_date && (
                        <div className="flex items-start gap-2">
                          <Calendar className="h-4 w-4 text-muted-foreground mt-0.5" />
                          <div>
                            <span className="text-muted-foreground">Tarih: </span>
                            <span className={`font-medium ${currentAlbum.date_modified ? 'text-red-600' : ''}`}>
                              {currentAlbum.album_date}
                            </span>
                          </div>
                        </div>
                      )}
                    </div>
                    
                    {/* Customer Note */}
                    {currentAlbum.customer_note && (
                      <div className="mt-3 p-3 bg-white/50 dark:bg-black/20 rounded border">
                        <p className="text-xs text-muted-foreground mb-1">Müşteri Notu:</p>
                        <p className="text-sm">{currentAlbum.customer_note}</p>
                      </div>
                    )}
                  </div>
                )}

                {/* Selection Summary */}
                <div className="p-4 bg-muted/50 rounded-lg">
                  <h4 className="font-medium text-sm mb-3">Seçim Özeti</h4>
                  <div className="grid grid-cols-3 gap-4 text-sm">
                    <div className="text-center p-2 bg-background rounded">
                      <div className="text-2xl font-bold">{currentAlbum.photo_count}</div>
                      <div className="text-xs text-muted-foreground">Toplam</div>
                    </div>
                    <div className="text-center p-2 bg-background rounded">
                      <div className="text-2xl font-bold text-green-600">{currentAlbum.selected_count}</div>
                      <div className="text-xs text-muted-foreground">Seçilen</div>
                    </div>
                    <div className="text-center p-2 bg-background rounded">
                      <div className="text-sm font-medium">{currentAlbum.confirmed_at ? formatDate(currentAlbum.confirmed_at.split('T')[0]) : '-'}</div>
                      <div className="text-xs text-muted-foreground">Onay Tarihi</div>
                    </div>
                  </div>
                </div>
                
                {/* Special Photos Section: Cover, Table, Album Template */}
                {(currentAlbum.cover_photo || currentAlbum.table_photo || currentAlbum.selected_template) && (
                  <div className="p-4 bg-gradient-to-r from-blue-50 to-purple-50 dark:from-blue-900/20 dark:to-purple-900/20 rounded-lg border border-blue-200 dark:border-blue-800">
                    <h4 className="font-medium text-sm mb-3 flex items-center gap-2">
                      <Image className="h-4 w-4 text-blue-500" />
                      Kapak, Tablo ve Albüm Modeli
                    </h4>
                    <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 gap-3">
                      {/* Cover Photo */}
                      <div className="space-y-1">
                        <p className="text-[10px] text-muted-foreground text-center truncate">Kapak</p>
                        {currentAlbum.cover_photo ? (
                          <div 
                            className="relative rounded-lg overflow-hidden border-2 border-blue-500 cursor-pointer hover:scale-105 transition-transform aspect-square"
                            onClick={() => setSelectedPreview({
                              url: `${BACKEND_URL}${currentAlbum.cover_photo.url}`,
                              original_name: currentAlbum.cover_photo.original_name || 'Kapak Fotoğrafı',
                              is_selected: true
                            })}
                          >
                            <img 
                              src={`${BACKEND_URL}${currentAlbum.cover_photo.thumbnail_url}`}
                              alt="Kapak"
                              className="w-full h-full object-cover"
                              loading="lazy"
                            />
                          </div>
                        ) : (
                          <div className="aspect-square rounded-lg border-2 border-dashed border-muted-foreground/30 flex items-center justify-center">
                            <span className="text-[8px] text-muted-foreground">Yok</span>
                          </div>
                        )}
                      </div>
                      
                      {/* Table Photo */}
                      <div className="space-y-1">
                        <p className="text-[10px] text-muted-foreground text-center truncate">Tablo</p>
                        {currentAlbum.table_photo ? (
                          <div 
                            className="relative rounded-lg overflow-hidden border-2 border-purple-500 cursor-pointer hover:scale-105 transition-transform aspect-square"
                            onClick={() => setSelectedPreview({
                              url: `${BACKEND_URL}${currentAlbum.table_photo.url}`,
                              original_name: currentAlbum.table_photo.original_name || 'Tablo Fotoğrafı',
                              is_selected: true
                            })}
                          >
                            <img 
                              src={`${BACKEND_URL}${currentAlbum.table_photo.thumbnail_url}`}
                              alt="Tablo"
                              className="w-full h-full object-cover"
                              loading="lazy"
                            />
                          </div>
                        ) : (
                          <div className="aspect-square rounded-lg border-2 border-dashed border-muted-foreground/30 flex items-center justify-center">
                            <span className="text-[8px] text-muted-foreground">Yok</span>
                          </div>
                        )}
                      </div>
                      
                      {/* Album Template */}
                      <div className="space-y-1">
                        <p className="text-[10px] text-muted-foreground text-center truncate">Albüm Modeli</p>
                        {currentAlbum.selected_template ? (
                          <div 
                            className="relative rounded-lg overflow-hidden border-2 border-green-500 aspect-square cursor-pointer hover:scale-105 transition-transform"
                            onClick={() => currentAlbum.selected_template.image_url && setSelectedPreview({
                              url: `${BACKEND_URL}${currentAlbum.selected_template.image_url}`,
                              original_name: currentAlbum.selected_template.name || 'Albüm Şablonu',
                              is_selected: false
                            })}
                          >
                            {currentAlbum.selected_template.image_url ? (
                              <img 
                                src={`${BACKEND_URL}${currentAlbum.selected_template.image_url}`}
                                alt={currentAlbum.selected_template.name}
                                className="w-full h-full object-cover"
                                loading="lazy"
                              />
                            ) : (
                              <div className="w-full h-full bg-muted flex items-center justify-center">
                                <BookImage className="h-6 w-6 text-muted-foreground" />
                              </div>
                            )}
                          </div>
                        ) : (
                          <div className="aspect-square rounded-lg border-2 border-dashed border-muted-foreground/30 flex items-center justify-center">
                            <span className="text-[8px] text-muted-foreground">Yok</span>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {/* Photo Grid */}
          {albumPhotosLoading ? (
            <div className="flex items-center justify-center h-64">
              <Loader2 className="h-8 w-8 animate-spin text-primary" />
            </div>
          ) : albumPhotos.length > 0 ? (
            <div>
              {/* Alt-Klasör Tab'ları */}
              <div className="mb-3 bg-slate-50 dark:bg-slate-900/50 border rounded-lg p-2" data-testid="album-subfolders-tabs">
                <div className="flex items-center gap-1 flex-wrap">
                  <button
                    onClick={() => setCurrentSubfolder('')}
                    className={`px-3 py-1.5 rounded-md text-xs font-medium transition ${currentSubfolder === '' ? 'bg-primary text-primary-foreground shadow-sm' : 'bg-white dark:bg-slate-800 hover:bg-slate-100'}`}
                    data-testid="subfolder-tab-main"
                  >
                    📁 Ana Albüm
                    <span className="ml-1 opacity-70">({(subfolders.find(s => s.name === '') || {}).count || 0})</span>
                  </button>
                  {subfolders.filter(s => s.name).map(sf => (
                    <button
                      key={sf.name}
                      onClick={() => setCurrentSubfolder(sf.name)}
                      className={`px-3 py-1.5 rounded-md text-xs font-medium transition ${currentSubfolder === sf.name ? 'bg-primary text-primary-foreground shadow-sm' : 'bg-white dark:bg-slate-800 hover:bg-slate-100'}`}
                      data-testid={`subfolder-tab-${sf.name}`}
                    >
                      📂 {sf.name}
                      <span className="ml-1 opacity-70">({sf.count})</span>
                    </button>
                  ))}
                  <button
                    onClick={() => { setNewSubfolderName(''); setShowNewSubfolderDialog(true); }}
                    className="px-3 py-1.5 rounded-md text-xs font-medium bg-sky-100 dark:bg-sky-900/30 text-sky-700 dark:text-sky-300 hover:bg-sky-200"
                    data-testid="new-subfolder-btn"
                  >
                    ➕ Yeni Alt-Klasör
                  </button>
                  {currentSubfolder && (
                    <button
                      onClick={async () => {
                        const newName = window.prompt(`"${currentSubfolder}" klasörünün yeni adı:`, currentSubfolder);
                        if (newName && newName.trim() && newName.trim() !== currentSubfolder) {
                          try {
                            await renameSubfolder(currentAlbum.id, currentSubfolder, newName.trim());
                            setCurrentSubfolder(newName.trim());
                            loadSubfolders();
                            toast.success('Alt-klasör yeniden adlandırıldı');
                          } catch (e) { toast.error('Hata: ' + e.message); }
                        }
                      }}
                      className="px-2 py-1.5 rounded-md text-xs font-medium bg-slate-200 dark:bg-slate-700 hover:bg-slate-300 text-slate-700 dark:text-slate-200"
                      title="Yeniden adlandır"
                    >
                      ✏️
                    </button>
                  )}
                </div>
                <p className="text-[10px] text-slate-400 mt-1.5">Yeni yüklenecek fotoğraflar seçili sekmeye eklenir. Alt-klasör oluşturmak: "➕ Yeni Alt-Klasör" veya var olan fotoğrafları taşımak için seçin.</p>
              </div>

              <div className="flex items-center justify-between mb-3">
                <h3 className="text-sm font-medium text-muted-foreground">
                  {currentSubfolder ? `📂 ${currentSubfolder}` : '📁 Ana Albüm'} Fotoğrafları ({albumPhotos.filter(p => (p.subfolder || '') === currentSubfolder).length})
                  {selectedPhotos.size > 0 && (
                    <span className="ml-2 text-primary">• {selectedPhotos.size} seçili</span>
                  )}
                </h3>
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={selectAllPhotos}
                  >
                    {selectedPhotos.size === albumPhotos.filter(p => (p.subfolder || '') === currentSubfolder).length ? 'Seçimi Kaldır' : 'Tümünü Seç'}
                  </Button>
                  {selectedPhotos.size > 0 && (
                    <>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => { setMoveTargetSubfolder(''); setShowMoveDialog(true); }}
                        data-testid="move-photos-btn"
                      >
                        📦 Taşı ({selectedPhotos.size})
                      </Button>
                      <Button
                        variant="destructive"
                        size="sm"
                        onClick={deleteSelectedPhotos}
                        disabled={isDeleting}
                      >
                        {isDeleting ? (
                          <Loader2 className="h-4 w-4 animate-spin mr-1" />
                        ) : (
                          <Trash2 className="h-4 w-4 mr-1" />
                        )}
                        Seçilenleri Sil ({selectedPhotos.size})
                      </Button>
                    </>
                  )}
                </div>
              </div>
              <div className="grid grid-cols-4 sm:grid-cols-5 md:grid-cols-6 lg:grid-cols-8 xl:grid-cols-10 gap-2">
                {albumPhotos.filter(p => (p.subfolder || '') === currentSubfolder).map((photo, index) => (
                  <div
                    key={photo.id}
                    className={`relative group rounded overflow-hidden border-2 transition-all cursor-pointer ${
                      selectedPhotos.has(photo.id)
                        ? 'border-primary ring-2 ring-primary/30'
                        : photo.is_selected
                          ? 'border-green-500 ring-1 ring-green-500/30'
                          : 'border-transparent hover:border-muted-foreground/30'
                    }`}
                  >
                    {/* Checkbox */}
                    <div 
                      className="absolute top-1 left-1 z-10"
                      onClick={(e) => {
                        e.stopPropagation();
                        togglePhotoSelection(photo.id);
                      }}
                    >
                      <div className={`h-5 w-5 rounded border-2 flex items-center justify-center transition-colors ${
                        selectedPhotos.has(photo.id) 
                          ? 'bg-primary border-primary' 
                          : 'bg-background/80 border-muted-foreground/50 hover:border-primary'
                      }`}>
                        {selectedPhotos.has(photo.id) && (
                          <Check className="h-3 w-3 text-white" />
                        )}
                      </div>
                    </div>
                    
                    <div 
                      className="aspect-square"
                      onClick={() => setSelectedPreview(photo)}
                    >
                      <img
                        src={photo.thumbnail_url || photo.url}
                        alt={photo.original_name}
                        className="w-full h-full object-cover"
                        loading="lazy"
                      />
                    </div>
                    {photo.is_selected && (
                      <div className="absolute top-1 right-1 h-4 w-4 rounded-full bg-green-500 flex items-center justify-center">
                        <Check className="h-3 w-3 text-white" />
                      </div>
                    )}
                    <div className="p-1 bg-background/90 text-[8px] truncate text-muted-foreground text-center" title={photo.original_name}>
                      {photo.original_name}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <div className="text-center py-12">
              <Image className="h-12 w-12 mx-auto mb-4 text-muted-foreground/30" />
              <h3 className="text-lg font-medium mb-2">Henüz fotoğraf yok</h3>
              <p className="text-muted-foreground mb-4">Fotoğraf yükleyerek başlayın</p>
            </div>
          )}

          {/* Delete Album */}
          <div className="pt-8 border-t border-border">
            <Button
              variant="destructive"
              onClick={handleDeleteAlbum}
              data-testid="delete-album-btn"
            >
              <Trash2 className="h-4 w-4 mr-2" />
              Albümü Sil
            </Button>
          </div>
        </>
      )}

      {/* Photo Preview Modal */}
      {selectedPreview && (
        <div
          className="fixed inset-0 z-50 bg-black/90 flex flex-col items-center justify-center p-4"
          onClick={() => setSelectedPreview(null)}
        >
          <Button
            variant="ghost"
            size="icon"
            className="absolute top-4 right-4 text-white hover:bg-white/20"
            onClick={() => setSelectedPreview(null)}
          >
            <X className="h-6 w-6" />
          </Button>
          {/* Filename at top */}
          <div className="absolute top-4 left-4 bg-black/60 px-4 py-2 rounded-lg">
            <p className="text-white text-sm font-medium">{selectedPreview.original_name}</p>
          </div>
          <img
            src={selectedPreview.preview_url || selectedPreview.url}
            alt={selectedPreview.original_name}
            className="max-w-full max-h-[80vh] object-contain"
            onClick={(e) => e.stopPropagation()}
          />
          {selectedPreview.is_selected && (
            <div className="absolute bottom-4 left-1/2 -translate-x-1/2 bg-primary text-white px-4 py-2 rounded-full flex items-center gap-2">
              <Check className="h-4 w-4" />
              Müşteri tarafından seçildi
            </div>
          )}
        </div>
      )}

      {/* Aynı İsimde Fotoğraf Uyarı Dialog */}
      <Dialog open={showDuplicateDialog} onOpenChange={setShowDuplicateDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-amber-600">
              <AlertTriangle className="h-5 w-5" />
              Aynı İsimde Fotoğraflar Var!
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground">
              Yüklemek istediğiniz <strong>{duplicateFiles.length}</strong> fotoğraf albümde zaten mevcut:
            </p>
            <div className="max-h-32 overflow-y-auto bg-muted/50 rounded-lg p-3 text-sm">
              {duplicateFiles.map((file, idx) => (
                <div key={idx} className="py-1 border-b last:border-0 border-border/50 text-amber-600">
                  ⚠️ {file.name}
                </div>
              ))}
            </div>
            <div className="bg-blue-50 dark:bg-blue-900/20 p-3 rounded-lg text-sm">
              <p><strong>Toplam:</strong> {pendingFiles.length} fotoğraf seçildi</p>
              <p><strong>Aynı isimli:</strong> {duplicateFiles.length} fotoğraf</p>
              <p><strong>Yeni:</strong> {pendingFiles.length - duplicateFiles.length} fotoğraf</p>
            </div>
            <p className="text-sm font-medium">Ne yapmak istiyorsunuz?</p>
            <div className="flex flex-col gap-2">
              <Button 
                variant="outline" 
                className="w-full justify-start"
                onClick={handleUploadOnlyNew}
                disabled={pendingFiles.length - duplicateFiles.length === 0}
              >
                <Check className="h-4 w-4 mr-2 text-green-600" />
                Sadece Yenileri Yükle ({pendingFiles.length - duplicateFiles.length} fotoğraf)
              </Button>
              <Button 
                variant="outline" 
                className="w-full justify-start"
                onClick={handleUploadWithRename}
              >
                <Edit className="h-4 w-4 mr-2 text-blue-600" />
                İsim Değiştirerek Hepsini Yükle ({pendingFiles.length} fotoğraf)
              </Button>
              <Button 
                variant="destructive" 
                className="w-full justify-start"
                onClick={handleCancelDuplicateUpload}
              >
                <X className="h-4 w-4 mr-2" />
                İptal Et
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
      
      {/* Panoramik Albüm Sihirbazı */}
      <PanoramicAlbumWizard
        isOpen={showPanoramicWizard}
        onClose={() => setShowPanoramicWizard(false)}
        albumId={currentAlbum?.id}
        allPhotos={albumPhotos}
        token={token}
        customerName={customer ? `${customer.bride_name} & ${customer.groom_name}` : 'Album'}
      />

      {/* Albüm Baskısı Gönder Dialog */}
      <PrintOrderDialog
        open={showPrintDialog}
        onOpenChange={setShowPrintDialog}
        customer={customer}
        album={currentAlbum}
        token={token}
        onCreated={() => toast.success('Siparişinizi "Baskı Siparişlerim" sayfasından takip edebilirsiniz')}
      />

      {/* Yeni Alt-Klasör Dialog */}
      <Dialog open={showNewSubfolderDialog} onOpenChange={setShowNewSubfolderDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>Yeni Alt-Klasör Oluştur</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <p className="text-xs text-slate-500">Örn: "Dış Çekim", "Kına", "Nikah". Oluşturunca aktif tab olarak seçilecek ve sonraki yüklemeler buraya gidecek.</p>
            <Input
              value={newSubfolderName}
              onChange={(e) => setNewSubfolderName(e.target.value)}
              placeholder="Alt-klasör adı"
              autoFocus
              onKeyDown={(e) => { if (e.key === 'Enter') { document.getElementById('confirm-new-subfolder-btn')?.click(); } }}
              data-testid="new-subfolder-input"
            />
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => setShowNewSubfolderDialog(false)} className="flex-1">İptal</Button>
              <Button
                id="confirm-new-subfolder-btn"
                onClick={() => {
                  const n = newSubfolderName.trim();
                  if (!n) { toast.error('Alt-klasör adı gerekli'); return; }
                  if (subfolders.some(s => s.name.toLowerCase() === n.toLowerCase())) {
                    toast.error('Bu isimde alt-klasör zaten var');
                    return;
                  }
                  setCurrentSubfolder(n);
                  // Yeni sub-folder henüz DB'de yok — ilk fotoğraf yüklenince oluşacak.
                  // Ama listede göstermek için local state'e ekle:
                  setSubfolders(prev => {
                    if (prev.some(s => s.name === n)) return prev;
                    return [...prev, { name: n, count: 0, selected_count: 0 }];
                  });
                  setShowNewSubfolderDialog(false);
                  toast.success(`"${n}" seçildi. Şimdi fotoğraf yükleyin.`);
                }}
                className="flex-1"
                data-testid="confirm-new-subfolder-btn"
              >
                Oluştur
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Fotoğrafları Alt-Klasöre Taşı Dialog */}
      <Dialog open={showMoveDialog} onOpenChange={setShowMoveDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>{selectedPhotos.size} Fotoğrafı Taşı</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <Label>Hangi alt-klasöre?</Label>
            <select
              value={moveTargetSubfolder}
              onChange={(e) => setMoveTargetSubfolder(e.target.value)}
              className="w-full h-9 rounded-md border border-input bg-background px-3 text-sm"
              data-testid="move-target-select"
            >
              <option value="">📁 Ana Albüm</option>
              {subfolders.filter(s => s.name).map(s => (
                <option key={s.name} value={s.name}>📂 {s.name}</option>
              ))}
            </select>
            <div className="flex gap-2 pt-1">
              <Button variant="outline" onClick={() => setShowMoveDialog(false)} className="flex-1">İptal</Button>
              <Button
                onClick={async () => {
                  try {
                    await movePhotosToSubfolder(currentAlbum.id, Array.from(selectedPhotos), moveTargetSubfolder);
                    toast.success(`${selectedPhotos.size} fotoğraf taşındı`);
                    setShowMoveDialog(false);
                    setSelectedPhotos(new Set());
                    await fetchAlbumPhotos(currentAlbum.id);
                    loadSubfolders();
                  } catch (e) { toast.error('Hata: ' + e.message); }
                }}
                className="flex-1"
                data-testid="confirm-move-btn"
              >
                Taşı
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
